import { useState } from "react";

import {
  PaymentElement,
  useStripe,
  useElements
} from "@stripe/react-stripe-js";

import {
  Loader,
  X,
  CalendarDays,
  Clock3,
  Users
} from "lucide-react";

import { toast } from "sonner";

export default function ReservationStripePaymentModal({
  isOpen,
  onClose,
  paymentIntent,
  reservationData,
  submitReservation,
  restaurant,
  loading = false
}) {

  const stripe = useStripe();

  const elements = useElements();

  const [processing, setProcessing] =
    useState(false);

  // ---------------------------------------------------
  // Don't Render
  // ---------------------------------------------------

  if (
    !isOpen ||
    !paymentIntent
  ) {
    return null;
  }

  // ---------------------------------------------------
  // Formatters
  // ---------------------------------------------------

  const formatDate = (date) => {

    if (!date) return "";

    return new Date(date)
      .toLocaleDateString(
        "en-IN",
        {
          weekday: "short",
          day: "numeric",
          month: "short",
          year: "numeric",
        }
      );
  };

  const formatTime = (date) => {

    if (!date) return "";

    return new Date(date)
      .toLocaleTimeString(
        "en-IN",
        {
          hour: "numeric",
          minute: "2-digit",
        }
      );
  };

  // ---------------------------------------------------
  // Handle Payment
  // ---------------------------------------------------

  const handleSubmit = async (e) => {

    e.preventDefault();

    if (
      !stripe ||
      !elements
    ) {

      toast.error(
        "Stripe not initialized"
      );

      return;
    }

    setProcessing(true);

    try {

      const { error } =
        await stripe.confirmPayment({

          elements,

          confirmParams: {

            return_url:
              `${window.location.origin}/reservation-success`,

            payment_method_data: {

              billing_details: {

                name:
                  reservationData.customer_name || "",

                email:
                  reservationData.customer_email || "",

                phone:
                  reservationData.customer_phone || ""
              }
            }
          },

          redirect: "if_required"
        });

      if (error) {

        console.error(error);

        toast.error(
          error.message ||
          "Payment failed"
        );

        return;
      }

      // -----------------------------------------
      // VERIFY PAYMENT STATUS
      // -----------------------------------------

      const retrievedIntent =
        await stripe.retrievePaymentIntent(
          paymentIntent.client_secret
        );

      if (
        retrievedIntent?.paymentIntent?.status ===
        "succeeded"
      ) {

        toast.success(
          "Reservation payment successful"
        );

        // -----------------------------------------
        // CREATE RESERVATION AFTER PAYMENT
        // -----------------------------------------

await submitReservation(
  retrievedIntent.paymentIntent.id
);

// Close modal
onClose();
      }

    } catch (error) {

      console.error(error);

      toast.error(
        error?.response?.data?.detail ||
        "Payment processing failed"
      );

    } finally {

      setProcessing(false);
    }
  };

  // ---------------------------------------------------
  // UI
  // ---------------------------------------------------

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50 p-4">

      <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">

        {/* HEADER */}

        <div className="mb-5 flex items-center justify-between">

          <div>

            <h2 className="text-2xl font-black text-gray-900">
              Reservation Payment
            </h2>

            <p className="text-sm text-gray-500 mt-1">
              Secure your table booking
            </p>

          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={processing}
            className="rounded-xl p-2 transition hover:bg-gray-100 disabled:opacity-50"
          >
            <X size={22} />
          </button>

        </div>

        {/* FORM */}

        <form
          onSubmit={handleSubmit}
          className="space-y-5"
        >

          {/* PAYMENT AMOUNT */}

          <div className="rounded-2xl border border-green-200 bg-green-50 p-5">

            <p className="text-sm text-gray-600">
              Reservation Advance
            </p>

            <p className="mt-1 text-3xl font-black text-green-700">
              {paymentIntent.currency_symbol}
              {paymentIntent.amount.toFixed(2)}
            </p>

          </div>

          {/* RESERVATION SUMMARY */}

          <div className="rounded-2xl border bg-gray-50 p-5 space-y-4">

            <div className="flex items-center gap-3">

              <CalendarDays
                size={18}
                className="text-gray-500"
              />

              <div>

                <p className="text-xs text-gray-500">
                  Reservation Date
                </p>

                <p className="font-semibold text-gray-900">
                  {formatDate(
                    reservationData.reservation_date
                  )}
                </p>

              </div>

            </div>

            <div className="flex items-center gap-3">

              <Clock3
                size={18}
                className="text-gray-500"
              />

              <div>

                <p className="text-xs text-gray-500">
                  Reservation Time
                </p>

                <p className="font-semibold text-gray-900">
                  {formatTime(
                    reservationData.reservation_date
                  )}
                </p>

              </div>

            </div>

            <div className="flex items-center gap-3">

              <Users
                size={18}
                className="text-gray-500"
              />

              <div>

                <p className="text-xs text-gray-500">
                  Guests
                </p>

                <p className="font-semibold text-gray-900">
                  {reservationData.party_size}
                </p>

              </div>

            </div>

          </div>

          {/* CUSTOMER DETAILS */}

          <div className="rounded-2xl bg-gray-50 p-4 text-sm">

            <div className="mb-2">

              <span className="font-semibold text-gray-700">
                Name:
              </span>{" "}

              <span className="text-gray-600">
                {reservationData.customer_name}
              </span>

            </div>

            <div className="mb-2">

              <span className="font-semibold text-gray-700">
                Email:
              </span>{" "}

              <span className="text-gray-600">
                {
                  reservationData.customer_email ||
                  "N/A"
                }
              </span>

            </div>

            <div>

              <span className="font-semibold text-gray-700">
                Phone:
              </span>{" "}

              <span className="text-gray-600">
                {
                  reservationData.customer_phone
                }
              </span>

            </div>

          </div>

          {/* STRIPE ELEMENT */}

          <div className="rounded-2xl border border-gray-300 bg-white p-4">

            <PaymentElement />

          </div>

          {/* PAY BUTTON */}

          <button
            type="submit"
            disabled={
              !stripe ||
              !elements ||
              processing ||
              loading
            }
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-black py-4 font-bold text-white transition hover:bg-gray-900 disabled:cursor-not-allowed disabled:bg-gray-400"
          >

            {processing || loading ? (
              <>
                <Loader
                  size={20}
                  className="animate-spin"
                />

                Processing...
              </>
            ) : (
              <>
                Pay{" "}
                {paymentIntent.currency_symbol}
                {paymentIntent.amount.toFixed(
                  2
                )}
              </>
            )}

          </button>

          {/* CANCEL */}

          <button
            type="button"
            onClick={onClose}
            disabled={
              processing ||
              loading
            }
            className="w-full rounded-2xl border border-gray-300 py-4 font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100"
          >
            Cancel
          </button>

          {/* FOOTER */}

          <p className="text-center text-xs text-gray-500">
            🔒 Secured by Stripe
          </p>

        </form>

      </div>

    </div>
  );
}